import { Component, HostListener, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { NgModel } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AHRService, Milestone, ReviewDateEntry } from '../../../domain/data-access-interfaces';
import { CanComponentDeactivate } from '../../program-review/workflow/unsaved-changes.guard';

interface RowVm {
	milestone: Milestone;
	date: Date | null;
	referenceOpen: Milestone | null;
}

@Component({
	selector: 'ahr-review-dates',
	templateUrl: './ahr-review-dates.component.html',
	styleUrls: ['./ahr-review-dates.component.scss']
})
export class AHRReviewDatesComponent implements OnInit, CanComponentDeactivate {

	@ViewChildren(NgModel) dateInputs!: QueryList<NgModel>;
	@ViewChild('yearSelect') yearSelect?: NgModel;

	years: number[] = [];
	selectedYear: number | null = null;
	milestones: Milestone[] = [];
	rows: RowVm[] = [];
	loading = false;
	saving = false;

	// Last-saved date per milestone key (ISO date or null = no date). Edits are
	// compared against this so the page knows when it has unsaved changes.
	private savedByKey = new Map<string, string | null>();

	creating = false;
	newYearInput: number | null = null;
	cloneSourceYear: number | null = null;

	private currentYearId: number | null = null;

	constructor(
		private readonly svc: AHRService,
		private readonly sb: MatSnackBar
	) { }

	ngOnInit(): void {
		// The current period of review comes from the server (honors the
		// configured cutoff and simulated clock) rather than local date math —
		// same source as the cases dashboard. The endpoint returns the
		// period-ending year; the review-date years key on periodOfReviewId
		// (= ending year + 1), so shift by 1.
		Promise.all([
			this.svc.getReviewDateYears(),
			this.svc.getCurrentPeriodOfReviewYear()
				.then(y => { const n = y ? +y : NaN; return isNaN(n) ? null : n + 1; })
				.catch(() => null)
		]).then(([years, currentYearId]) => {
			this.years = years;
			const current = currentYearId ?? this.currentPeriodOfReview();
			this.currentYearId = current;
			if (years.length === 0) {
				this.selectedYear = null;
			} else if (years.includes(current)) {
				this.selectYear(current);
			} else {
				// No entry for the current period — open the nearest configured
				// year (e.g. current 2026 with [2028, 2027] → 2027), preferring
				// the later year on a tie.
				this.selectYear(this.closestYear(current, years));
			}
		});
	}

	private closestYear(target: number, years: number[]): number {
		return years.reduce((best, y) => {
			const dy = Math.abs(y - target);
			const db = Math.abs(best - target);
			if (dy < db) return y;
			if (dy === db) return Math.max(y, best);
			return best;
		});
	}

	private currentPeriodOfReview(): number {
		// Mirrors ReviewScheduleAggregate.PeriodOfReview.ofMoment:
		// July onward rolls into next academic year.
		const today = new Date();
		return today.getMonth() >= 6 ? today.getFullYear() + 1 : today.getFullYear();
	}

	/** Year-dropdown handler. Switching years discards in-progress edits just
	 *  like leaving the page, so it goes through the same confirm. On cancel the
	 *  one-way [ngModel] binding won't re-sync the native <select> (the model
	 *  value is unchanged), so push the kept year back through the control. */
	onYearChange(year: number): void {
		if (year === this.selectedYear) return;
		if (!this.confirmDiscardIfDirty()) {
			this.yearSelect?.control.setValue(this.selectedYear, { emitEvent: false });
			return;
		}
		this.selectYear(year);
	}

	selectYear(year: number): void {
		this.selectedYear = year;
		this.loading = true;
		this.svc.getReviewDates(year).then(payload => {
			this.milestones = payload.milestones;
			const byKey = new Map<string, Date>();
			payload.dates.forEach(d => {
				if (d.date) byKey.set(d.milestoneKey, this.parseIsoDate(d.date));
			});
			this.rows = payload.milestones.map(m => ({
				milestone: m,
				date: byKey.get(m.key) ?? null,
				referenceOpen: m.referenceOpenKey
					? payload.milestones.find(x => x.key === m.referenceOpenKey) ?? null
					: null
			}));
			this.captureSaved();
			this.loading = false;
		});
	}

	/** Where the calendar opens when the user clicks an empty cell. When the
	 *  row already has a value, return null so Material opens to the value's
	 *  month without drawing a second highlight.
	 *   - First row, year 2027 → Nov 2026 (program start year)
	 *   - Subsequent rows → most recent earlier row's date, else fall back to first-row default
	 */
	startAt(index: number): Date | null {
		if (this.rows[index].date) return null;
		for (let i = index - 1; i >= 0; i--) {
			const d = this.rows[i].date;
			if (d) return d;
		}
		const programStartYear = (this.selectedYear ?? this.currentYearId ?? this.currentPeriodOfReview()) - 1;
		return new Date(programStartYear, 10, 1); // Nov 1 (month is 0-indexed)
	}

	daysFromOpen(row: RowVm): number | null {
		if (!row.referenceOpen || !row.date) return null;
		const openRow = this.rows.find(r => r.milestone.key === row.referenceOpen!.key);
		if (!openRow || !openRow.date) return null;
		const ms = row.date.getTime() - openRow.date.getTime();
		return Math.round(ms / (1000 * 60 * 60 * 24));
	}

	startCreate(): void {
		// Entering create mode replaces the editor and discards in-progress edits.
		if (!this.confirmDiscardIfDirty()) return;
		this.creating = true;
		const last = this.years.length > 0 ? this.years[0] : null;
		// years/cloneSourceYear are program-year ids; newYearInput is the
		// period-of-review-ending year shown to the admin (id − 1).
		const nextId = last !== null ? last + 1 : (this.currentYearId ?? this.currentPeriodOfReview());
		this.newYearInput = nextId - 1;
		this.cloneSourceYear = last;
	}

	cancelCreate(): void {
		this.creating = false;
		this.newYearInput = null;
		this.cloneSourceYear = null;
	}

	confirmCreate(): void {
		const endingYear = this.newYearInput;
		if (endingYear === null || isNaN(endingYear)) {
			this.sb.open("Enter a valid year", undefined, { duration: 3000 });
			return;
		}
		// The admin enters the period-of-review-ending year; the stored key is
		// the program-year id (ending year + 1), which must match workflow.Case.
		const target = endingYear + 1;
		if (this.years.includes(target)) {
			this.sb.open(`Year ${endingYear} already exists`, undefined, { duration: 3000 });
			return;
		}

		const finishWith = (year: number) => {
			this.years = [year, ...this.years.filter(y => y !== year)].sort((a, b) => b - a);
			this.creating = false;
			this.newYearInput = null;
			this.cloneSourceYear = null;
			this.selectYear(year);
		};

		if (this.cloneSourceYear !== null && this.cloneSourceYear !== undefined) {
			this.svc.cloneReviewDatesFrom(this.cloneSourceYear, target).then(result => {
				result.match(
					() => finishWith(target),
					err => this.sb.open(`Clone failed: ${err}`, undefined, { duration: 4000 }));
			});
		} else {
			this.svc.saveReviewDates(target, []).then(result => {
				result.match(
					() => finishWith(target),
					err => this.sb.open(`Save failed: ${err}`, undefined, { duration: 4000 }));
			});
		}
	}

	/** True if any row's input has a parse error (typed nonsense) or its bound
	 *  value is an Invalid Date. Reads form-control state directly because
	 *  Material registers the parse error on the control, not on the model. */
	get hasInvalidDate(): boolean {
		if (this.dateInputs) {
			for (const ng of this.dateInputs.toArray()) {
				if (ng.errors?.['matDatepickerParse']) return true;
			}
		}
		return this.rows.some(r => r.date instanceof Date && isNaN(r.date.getTime()));
	}

	save(): void {
		if (this.selectedYear === null) return;
		// Force any focused input to flush its current value into ngModel/form state
		// before we read validity (Material updates on blur/change).
		(document.activeElement as HTMLElement | null)?.blur?.();
		if (this.hasInvalidDate) {
			this.sb.open("Fix invalid dates before saving.", undefined, { duration: 4000 });
			return;
		}
		const entries: ReviewDateEntry[] = this.rows.map(r => ({
			milestoneKey: r.milestone.key,
			date: (r.date instanceof Date && !isNaN(r.date.getTime())) ? this.toIsoDate(r.date) : null
		}));
		this.saving = true;
		this.svc.saveReviewDates(this.selectedYear, entries).then(result => {
			this.saving = false;
			result.match(
				() => {
					// Only now do the edits become the saved baseline.
					this.captureSaved();
					this.sb.open("Review dates saved", undefined, { duration: 3000 });
				},
				err => this.sb.open(`Save failed: ${err}`, undefined, { duration: 4000 }));
		});
	}

	/** Snapshot the current row dates as the saved baseline. */
	private captureSaved(): void {
		this.savedByKey = new Map(this.rows.map(r => [r.milestone.key, this.rowDateIso(r)]));
	}

	private rowDateIso(r: RowVm): string | null {
		return r.date instanceof Date && !isNaN(r.date.getTime()) ? this.toIsoDate(r.date) : null;
	}

	/** True when an in-progress edit differs from the last-saved state. */
	get hasUnsavedChanges(): boolean {
		return this.rows.some(r => this.rowDateIso(r) !== (this.savedByKey.get(r.milestone.key) ?? null));
	}

	// Browser-level guard (refresh / close / hard navigation). The SPA in-app
	// guard is the route's CanDeactivate (see unsaved-changes.guard.ts), which
	// calls canDeactivate() below.
	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(e: BeforeUnloadEvent): void {
		if (this.hasUnsavedChanges) {
			e.preventDefault();
			e.returnValue = '';
		}
	}

	/** Returns true if it's safe to discard edits; otherwise prompts to confirm.
	 *  Shared by the route guard (canDeactivate) and the year switcher. */
	private confirmDiscardIfDirty(): boolean {
		if (!this.hasUnsavedChanges) return true;
		return confirm('You have unsaved review-date changes that will be lost. Continue?');
	}

	/** Returns true if it's safe to leave; otherwise prompts to confirm. */
	canDeactivate(): boolean {
		return this.confirmDiscardIfDirty();
	}

	private toIsoDate(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private parseIsoDate(input: string): Date {
		// Server may return "2026-11-09" or "2026-11-09T00:00:00". Take the date part.
		const s = input.indexOf('T') > 0 ? input.substring(0, input.indexOf('T')) : input;
		const [y, m, d] = s.split('-').map(n => parseInt(n, 10));
		return new Date(y, m - 1, d);
	}
}
